import { useEffect, useRef, useState } from 'react';
import { uniq } from 'underscore';
import { Button } from 'react-bootstrap';
import { get, useFormContext } from 'react-hook-form';
import t from 'react-translate';
import NvTextInput, { Props as NvTextInputProps } from 'shared/components/inputs/nv-text-input';
import NvTooltip from 'shared/components/nv-tooltip';

const labelClass = 'text-small gray-3 m-0';

export const parseEmails = (input: string | string[] | undefined): string[] => {
  if (!input) return [];
  if (Array.isArray(input)) {
    return input.map(e => e.trim()).filter(e => e);
  }
  return input.split(',').map(e => e.trim()).filter(e => e);
};

type ValidationResult = {
  isValid: string[];
  isInvalid: string[];
};

export type HostValidatorProps = Omit<NvTextInputProps, 'withForm' | 'name' | 'ref'> & {
  formName: string;
  disabled: boolean;
  onValidHost: (isValid: boolean) => void;
  label: string;
  host?: string;
  isMultipleHosts?: boolean;
  description?: string;
  tooltip?: string;
  onValidate: (hostUserIds: string) => Promise<ValidationResult>;
  onValidationError: (invalidHosts: string[]) => void;
  showValidator?: boolean;
};

const HostValidator = (props: HostValidatorProps) => {
  const {
    formName,
    disabled,
    host: initialHostValue,
    onValidHost,
    label,
    isMultipleHosts,
    description,
    onValidate,
    onValidationError,
    showValidator,
    tooltip,
    ...restProps
  } = props;

  const { watch, setError, formState } = useFormContext();

  const hostUserEmailIds = watch(formName);

  const initialValidatedHosts = parseEmails(initialHostValue);

  const [validating, setValidating] = useState(false);
  const [validatedHosts, setValidatedHosts] = useState<string[]>(initialValidatedHosts);
  const [invalidHosts, setInvalidHosts] = useState<string[]>([]);

  const handleValidate = () => {
    setValidating(true);

    onValidate(hostUserEmailIds)
      .then((payload) => {
        const newInvalids = payload?.isInvalid || [];
        const newValids = payload?.isValid || [];

        if (newInvalids.length > 0) {
          setError(formName, { type: 'validate' });
          setInvalidHosts(prev => uniq([...prev, ...newInvalids]));
          onValidationError(newInvalids);
        }

        if (newValids.length > 0) {
          setValidatedHosts(prev => uniq([...prev, ...newValids]));
        }

        setValidating(false);
      })
      .catch(() => {
        setError(formName, { type: 'custom' });
        setValidating(false);
      });
  };

  const hasErrors = !!get(formState.errors, formName);
  const parsedEmails = parseEmails(hostUserEmailIds);
  const isEmpty = parsedEmails.length === 0;
  const allValidated = !isEmpty && parsedEmails.every(email => validatedHosts.includes(email));
  // Optional fields (isMultipleHosts) are valid when empty; required fields need content + validation
  const isValid = (isMultipleHosts && isEmpty) || allValidated;

  const hasInvalidHost = parsedEmails.some(email => invalidHosts.includes(email));

  const onValidHostRef = useRef(onValidHost);
  onValidHostRef.current = onValidHost;

  useEffect(() => {
    onValidHostRef.current?.(isValid);
  }, [isValid]);

  return (
    <div className='mb-4'>
      <label className={labelClass}>{label}</label>
      <div className='d-flex w-100'>
        <NvTooltip text={tooltip} placement='top' enabled={!!tooltip}>
          <div className='w-100'>
            <NvTextInput
              {...restProps}
              withForm
              required={!isMultipleHosts}
              type={isMultipleHosts ? 'text' : 'email'}
              name={formName}
              autoComplete='off'
              disabled={disabled}
              ariaLabel={restProps.ariaLabel ?? label}
              placeholder={restProps.placeholder}
              id={formName}
            />
          </div>
        </NvTooltip>
        {showValidator && (
          <div
            css={{ minWidth: '110px' }}
            className='d-flex ml-2 align-items-center bold justify-content-center'
          >
            {allValidated ? (
              <span className='text-success'>
                {t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.HOST_VALIDATED()}
              </span>
            ) : (
              <Button
                variant='primary'
                className='w-100'
                type='button'
                disabled={
                  !hostUserEmailIds
                  || validating
                  || disabled
                  || hasErrors
                  || hasInvalidHost
                }
                onClick={handleValidate}
              >
                {validating ? t.FORM.VALIDATING() : t.FORM.VALIDATE()}
              </Button>
            )}
          </div>
        )}
      </div>
      {description && (
        <span className='label gray-3'>{description}</span>
      )}
    </div>
  );
};

export default HostValidator;
